import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Star, Plus, Trash2, Zap, BookMarked, X } from 'lucide-react';

// 'YourReGen Approved' is ONLY shown for approved Meal Library meals (scope 'library', approved=true from the API).
const SCOPE_LABEL = { personal: 'My Template', coach: 'Coach', library: 'YourReGen Approved' };
const SCOPE_COLOR = { personal: '#00C8FF', coach: '#FF6FA8', library: '#65A30D' };

export function MealTemplates({ foods, reload }) {
  const [presets, setPresets] = useState([]);
  const [editing, setEditing] = useState(null); // { preset, items:[{food_id,grams}], slot }

  const [library, setLibrary] = useState([]);
  const load = async () => {
    const [r, l] = await Promise.all([api.get('/meal-presets'), api.get('/meal-library/templates').catch(() => ({ data: [] }))]);
    setPresets(r.data); setLibrary(l.data || []);
  };
  useEffect(() => { load(); }, []);

  const foodById = (id) => foods.find(f => f.id === id);
  const favorites = presets.filter(p => p.is_favorite);

  const logUrl = (p) => (p.scope === 'library' ? `/meal-library/${p.meal_id}/log` : `/meal-presets/${p.id}/log`);
  const quickLog = async (p, slot) => {
    await api.post(logUrl(p), { slot: slot || p.slot || 'Meal 1' });
    toast.success(`${p.name} logged`);
    reload?.();
  };
  const toggleFav = async (p) => {
    const r = await api.post(`/meal-presets/${p.id}/favorite`);
    setPresets(ps => ps.map(x => x.id === p.id ? { ...x, is_favorite: r.data.is_favorite } : x));
  };
  const del = async (p) => {
    if (!confirm(`Delete template "${p.name}"?`)) return;
    await api.delete(`/meal-presets/${p.id}`); load();
  };
  const openEdit = (p) => setEditing({ preset: p, slot: p.slot || 'Meal 1', items: (p.items || []).map(i => ({ food_id: i.food_id, grams: i.grams, food: i.food })) });

  const editTotals = () => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    (editing?.items || []).forEach(it => {
      const f = it.food || foodById(it.food_id); if (!f) return;
      const g = (parseFloat(it.grams) || 0) / 100;
      t.calories += (f.calories_per_100g || 0) * g; t.protein += (f.protein_per_100g || 0) * g;
      t.carbs += (f.carbs_per_100g || 0) * g; t.fat += (f.fat_per_100g || 0) * g;
    });
    return t;
  };
  const logEdited = async () => {
    await api.post(logUrl(editing.preset), { slot: editing.slot, items: editing.items.map(i => ({ food_id: i.food_id, grams: parseFloat(i.grams) || 0 })) });
    toast.success(`${editing.preset.name} logged`); setEditing(null); reload?.();
  };

  return (
    <div className="space-y-4">
      {/* Quick Add Favorites */}
      {favorites.length > 0 && (
        <div className="regen-card-elevated p-4" data-testid="quick-add-favorites">
          <div className="flex items-center gap-2 mb-2.5"><Zap className="w-4 h-4 text-[#FFB800]" /><div className="label-mini">Quick Add Favorites</div></div>
          <div className="flex flex-wrap gap-2">
            {favorites.map(p => (
              <button key={p.id} onClick={() => quickLog(p)} data-testid={`quick-fav-${p.id}`}
                className="pill bg-[#FFB800]/15 text-[#FFB800] font-semibold flex items-center gap-1.5 px-3 py-2 active:scale-95 transition">
                <Star className="w-3 h-3 fill-current" /> {p.name}
                <span className="text-[10px] font-mono opacity-70">{Math.round(p.totals?.calories || 0)}kcal</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground" data-testid="meal-library-explainer">Pick a meal from the YourReGen Meal Library and log it as is or with your own portions.</p>
      <div className="flex items-center gap-2">
        <BookMarked className="w-4 h-4 text-[#65A30D]" />
        <div className="label-mini">YourReGen Meal Library</div>
      </div>
      {library.length === 0
        ? <div className="regen-card-elevated p-4 text-center text-sm text-muted-foreground" data-testid="library-empty">Coach JP is adding approved meals to the library — check back soon.</div>
        : <LibraryList library={library} onLog={quickLog} onEdit={openEdit} />}

      <div className="flex items-center gap-2">
        <BookMarked className="w-4 h-4 text-[#FFB800]" />
        <div className="label-mini">My Meal Templates</div>
      </div>

      {presets.length === 0 ? (
        <div className="regen-card-elevated p-6 text-center text-sm text-muted-foreground">
          No templates yet. Build a meal and tap <span className="text-[#FFB800] font-semibold">Save as Template</span> to reuse it in one tap.
        </div>
      ) : (
        <div className="space-y-2">
          {presets.map(p => {
            const t = p.totals || {};
            return (
              <div key={p.id} className="regen-card-elevated p-3" data-testid={`template-${p.id}`}>
                <div className="flex items-start gap-3">
                  <button onClick={() => toggleFav(p)} data-testid={`fav-toggle-${p.id}`} className="mt-0.5">
                    <Star className={`w-4 h-4 ${p.is_favorite ? 'fill-[#FFB800] text-[#FFB800]' : 'text-muted-foreground'}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold truncate">{p.name}</div>
                      <span className="pill text-[9px]" style={{ background: `${SCOPE_COLOR[p.scope]}1a`, color: SCOPE_COLOR[p.scope] }}>{SCOPE_LABEL[p.scope]}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">{(p.items || []).map(i => `${i.food?.name} ${i.grams}g`).join(' · ')}</div>
                    <div className="text-[11px] mt-1 flex gap-2 font-mono">
                      <span className="text-[#FFB800]">{Math.round(t.calories || 0)} kcal</span>
                      <span className="text-[var(--c-green-ink)]">P {Math.round(t.protein || 0)}</span>
                      <span className="text-[#00C8FF]">C {Math.round(t.carbs || 0)}</span>
                      <span className="text-[#7DD3FC]">F {Math.round(t.fat || 0)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-2.5">
                  <Button onClick={() => quickLog(p)} data-testid={`log-asis-${p.id}`} className="flex-1 h-9 bg-[#FFB800] text-[#081726] font-semibold text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Log As Is</Button>
                  <Button onClick={() => openEdit(p)} data-testid={`log-edit-${p.id}`} variant="outline" className="h-9 border-[var(--c-hair)] text-xs">Edit Portions</Button>
                  {p.scope === 'personal' && <Button onClick={() => del(p)} variant="outline" className="h-9 border-red-500/30 text-red-400 px-2.5"><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit portions dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md">
          <DialogHeader><DialogTitle>{editing?.preset?.name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3" data-testid="edit-portions-dialog">
              <p className="text-xs text-muted-foreground">Adjust portions before logging.</p>
              {editing.items.map((it, idx) => {
                const f = it.food || foodById(it.food_id);
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-1 text-sm">{f?.name || '—'}</div>
                    <div className="relative w-24">
                      <Input type="number" value={it.grams} onChange={e => setEditing({ ...editing, items: editing.items.map((x, i) => i === idx ? { ...x, grams: e.target.value } : x) })}
                        data-testid={`edit-grams-${idx}`} className="bg-[var(--c-film)] border-[var(--c-hair)] h-10 font-mono text-center pr-6" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">g</span>
                    </div>
                  </div>
                );
              })}
              <div className="rounded-xl bg-[var(--c-film)] p-2.5 text-center font-mono text-sm">
                {(() => { const t = editTotals(); return <span><span className="text-[#FFB800]">{Math.round(t.calories)} kcal</span> · P{Math.round(t.protein)} C{Math.round(t.carbs)} F{Math.round(t.fat)}</span>; })()}
              </div>
              <Button onClick={logEdited} data-testid="log-edited-btn" className="w-full h-11 bg-[#FFB800] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-1" /> Log Meal</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateCard({ p, onLog, onEdit }) {
  const t = p.totals || {};
  return (
    <div className="regen-card-elevated p-3" data-testid={`library-template-${p.meal_id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <div className="text-sm font-semibold truncate">{p.name}</div>
        {p.approved && <span className="pill text-[9px] shrink-0" style={{ background: '#65A30D1a', color: '#65A30D' }} data-testid={`approved-badge-${p.meal_id}`}>YourReGen Approved</span>}
        {p.category && <span className="text-[10px] text-muted-foreground shrink-0">{p.category}</span>}
      </div>
      <div className="text-[11px] text-muted-foreground truncate mt-0.5">{(p.items || []).map(i => `${i.food?.name} ${i.grams}g`).join(' · ')}</div>
      <div className="text-[11px] mt-1 flex gap-2 font-mono">
        <span className="text-[#FFB800]">{Math.round(t.calories || 0)} kcal</span>
        <span className="text-[var(--c-green-ink)]">P {Math.round(t.protein || 0)}</span>
        <span className="text-[#00C8FF]">C {Math.round(t.carbs || 0)}</span>
        <span className="text-[#7DD3FC]">F {Math.round(t.fat || 0)}</span>
      </div>
      <div className="flex gap-2 mt-2.5">
        <Button onClick={() => onLog(p)} data-testid={`lib-log-asis-${p.meal_id}`} className="flex-1 h-9 bg-[#FFB800] text-[#081726] font-semibold text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Log As Is</Button>
        <Button onClick={() => onEdit(p)} data-testid={`lib-log-edit-${p.meal_id}`} variant="outline" className="h-9 border-[var(--c-hair)] text-xs">Edit Portions</Button>
      </div>
    </div>
  );
}

function LibraryList({ library, onLog, onEdit }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [all, setAll] = useState(false);
  const cats = ['All', ...Array.from(new Set(library.map(p => p.category).filter(Boolean)))];
  const shown = library.filter(p => (cat === 'All' || p.category === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));
  const list = all || q ? shown : shown.slice(0, 8);
  return (
    <div className="space-y-2" data-testid="library-templates">
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search approved meals…" data-testid="library-search"
        className="w-full h-9 px-3 rounded-lg bg-regen-card border border-[var(--c-hair)] text-sm" />
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {cats.map(c => (
          <button key={c} onClick={() => setCat(c)} data-testid={`library-cat-${c.toLowerCase()}`}
            className={`pill shrink-0 border text-xs ${cat === c ? 'bg-[#65A30D] text-white border-[#65A30D]' : 'border-[var(--c-hair)] text-muted-foreground'}`}>{c}</button>
        ))}
      </div>
      {list.map(p => <TemplateCard key={p.id} p={p} onLog={onLog} onEdit={onEdit} />)}
      {!q && shown.length > list.length && (
        <button onClick={() => setAll(true)} data-testid="library-show-all" className="w-full text-xs text-[#65A30D] py-2">Show all {shown.length} meals</button>
      )}
      {shown.length === 0 && <div className="text-xs text-muted-foreground text-center py-3">No approved meals match.</div>}
    </div>
  );
}
